import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import { handleApiError } from "@utils/errorHandler";
import {
  TABLE_ADMIN_LIST,
  FILTER_TYPE_BUTTON,
  FILTER_TYPE_MULTI_SELECT_DROPDOWN,
} from "@utils/constants";
import EncloseTable from "@shared/baseUI/EnhancedTable/EncloseTable";
import Action from "./components/Action/Action";
import Country from "./components/Country/Country";
import GeneralInfo from "./components/GeneralInfo/GeneralInfo";
import { columnHeader, isEmpty } from "@utils/utils";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import CustomColumnFilter from "@shared/baseUI/CustomColumnFilter/CustomColumnFilter"

const PrepareDataTable = (props) => {
  const {
    t,
    isRefreshList,
    updateRefeshStatus,
    columnList,
    createNewSpecificCondition,
    countries,
  } = props;


  const [status, setStatus] = useState(1);
  const [data, setData] = useState([]);

  const [skipPageReset, setSkipPageReset] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});
  const sortedCountryList = [...countries]
    .sort((a, b) => a.label.localeCompare(b.label))
    .filter((country) => country.platform === 1);
  let columns = [];


  columns = [
    {
      Header: t("ID"),
      title: "ID",
      accessor: "id",
      disableFilters: true,
      Cell: ({ cell: { value } }) => <GeneralInfo data={value} column="id" />,
    },
    {
      Header: t("ADMIN_NAME"),
      title: "ADMIN_NAME",
      accessor: "name",
      disableFilters: true,
      Cell: ({ cell: { value } }) => <GeneralInfo data={value} column="name" />,
    },
    {
      Header: t("ADMIN_EMAIL"),
      title: "ADMIN_EMAIL",
      accessor: "email",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="email" />
      ),
    },
    {
      Header: t("COUNTRIES"),
      title: "COUNTRIES",
      accessor: "country",
      Cell: ({ cell: { value } }) => <Country data={value} />,
      Filter: (filterProps) => (
        <CustomColumnFilter
          type={FILTER_TYPE_MULTI_SELECT_DROPDOWN}
          label={t("COUNTRY")}
          placeholder={t("SEARCH")}
          name="country"
          labelClass="admins-list__country-label"
          containerClass="admins-list__country-dropdown-container"
          inputClass=""
          defaultOption={[]}
          options={sortedCountryList}
          // eslint-disable-next-line react/destructuring-assignment
          column={filterProps.column}
        />
      ),
    },
    {
      Header: t("ADMIN_IS_TECH"),
      title: "ADMIN_IS_TECH",
      accessor: "isTech",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="isTech" />
      ),
    },
    {
      Header: t("ADMIN_LAST_LOGIN"),
      title: "ADMIN_LAST_LOGIN",
      accessor: "lastLogin",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="lastLogin" />
      ),
    },
    {
      Header: t("ACTIONS"),
      title: "ACTIONS",
      accessor: "action",
      Cell: ({ cell: { value, column } }) => (
        <Action
          data={value}
          column={column}
          updateRefeshStatus={updateRefeshStatus}
        />
      ),
      disableFilters: true,
    },
  ];

  columns = columnHeader(columns, columnList);

  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({ globalFilter, sort, appliedFilters }) => {
    setRequestData({
      globalFilter,
      sort,
      appliedFilters,
    });
    // save request data,
    let country = [];
    if (appliedFilters !== undefined) {
      const countryFilter = appliedFilters.find(
        (filterItem) => filterItem.column === "country"
      );
      country =
        countryFilter?.value.length > 0
          ? countryFilter?.value
          : ["*", sortedCountryList.map((obj) => obj.id).join()];
    }
    // Set the loading state
    setLoading(true);
    axios
      .get(
        `/general/admins?countries=[${country}]&status=${status}&orderBy=${
          sort ? sort.dir : "desc"
        }&orderColumn=${sort ? sort.column : "id"}&limit=-1`
      )
      .then((response) => {
        if (response?.data?.admins.length > 0) {
          const adminsList = response?.data?.admins;
          const responseData = adminsList.map((adminList) => {
            const tempData = { ...adminList };
            tempData.action = { ...tempData };
            tempData.name = {
              name: tempData.firstName + " " + tempData.lastName,
              status: tempData.isActive,
            };
            tempData.email = {
              email: tempData.email,
              status: tempData.isActive,
            };
            tempData.isTech = {
              isTech: tempData.isTech,
              status: tempData.isActive,
            };
            tempData.lastLogin = {
              lastLogin: tempData.lastLogin,
              status: tempData.isActive,
            };
            tempData.id = {
              id: tempData.id,
              status: tempData.isActive,
            };
            tempData.country = {
              countryInfo: tempData.countries,
              status: tempData.isActive,
            };
            return tempData;
          });
          setData([...responseData]);
        } else setData([]);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        handleApiError(error);
      });
  };

  const changeStatus = (label) => {
    let statusNew = status === 1 ? 2 : 1;
    setStatus(statusNew);
  };

  useEffect(() => {
    // everyTime we apply an custom filter we reset the page to 1
    if (!isEmpty(requestData)) {
      fetchRecords({
        globalFilter: requestData.globalFilter,
        sort: requestData.sort,
        appliedFilters: requestData.appliedFilters,
      });
    }
  }, [status]);

  return (
    <EncloseTable
      type={TABLE_ADMIN_LIST}
      columns={columns}
      data={data}
      setData={setData}
      initialPageSize={100}
      createNewAction={createNewSpecificCondition}
      isRefreshDataAgain={isRefreshList}
      setSkipPageReset={setSkipPageReset}
      skipPageReset={skipPageReset}
      pageCount={pageCount}
      fetchRecords={fetchRecords}
      loading={loading}
      defaultSortOptions={[{ id: "id", desc: true, dir: "desc" }]}
      extraFilters={[
        {
          type: FILTER_TYPE_BUTTON,
          label: "ADMINS_FILTER_ACTIVE_ONLY",
          name: "vat",
          labelClass: "admins-list__admins-list-label",
          containerClass: "admins-list__admins-list-dropdown-container",
          imageEnableIcon: enableIcon,
          imageDisableIcon: disableIcon,
          status,
          changeStatus: { changeStatus },
        },
      ]}
    />
  );
};

PrepareDataTable.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
  columnList: PropTypes.array.isRequired,
};

export default withTranslation()(PrepareDataTable);
